import React, { useState, useRef, useCallback, useEffect } from "react";
import { Header } from "semantic-ui-react";
import "./Dashboard.css";
import DijonCard from "./DijonCard";
import axiosInstance from "../../lib/auth/axiosApi";

// Hold rather than tap: this thing wakes someone up with a siren, so a stray
// tap or a phone in a pocket shouldn't be able to fire it.
const HOLD_MS = 1500;

function DijonEmergency(props) {
  const [progress, setProgress] = useState(0);   // 0..1 while holding
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const startedAt = useRef(null);
  const rafId = useRef(null);

  const clearHold = useCallback(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = null;
    startedAt.current = null;
  }, []);

  // Don't leave an animation frame running if the card is closed mid-hold.
  useEffect(() => clearHold, [clearHold]);

  const fire = useCallback(async () => {
    clearHold();
    setProgress(1);
    setSending(true);
    setError(null);
    try {
      const res = await axiosInstance.post("/emergency/alarm/", {});
      if (!res.data || !res.data.success) throw new Error("failed");
      setSent(true);
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        "Couldn't reach the alarm. Call him instead.";
      setError(msg);
      setProgress(0);
    } finally {
      setSending(false);
    }
  }, [clearHold]);

  const tick = useCallback(() => {
    if (startedAt.current === null) return;
    const pct = Math.min(1, (performance.now() - startedAt.current) / HOLD_MS);
    setProgress(pct);
    if (pct >= 1) {
      fire();
      return;
    }
    rafId.current = requestAnimationFrame(tick);
  }, [fire]);

  const onDown = useCallback(
    (e) => {
      e.preventDefault();
      if (sending || sent) return;
      setError(null);
      startedAt.current = performance.now();
      rafId.current = requestAnimationFrame(tick);
    },
    [sending, sent, tick]
  );

  const onUp = useCallback(() => {
    if (sending || sent) return;
    clearHold();
    setProgress(0);
  }, [sending, sent, clearHold]);

  const reset = () => {
    setSent(false);
    setProgress(0);
    setError(null);
  };

  return (
    <div id="emergency">
      <DijonCard
        width={"100%"}
        height={"82vh"}
        activeCards={props.activeCards}
        setActiveCards={props.setActiveCards}
        name={"emergency"}
        content={
          <div
            className={"container mx-auto px-4"}
            style={{ height: "100%", overflow: "auto", padding: "20px" }}
          >
            <Header
              as="h2"
              textAlign="center"
              style={{ color: "#fff", marginBottom: "8px" }}
            >
              🚨 Emergency
            </Header>

            {sent ? (
              <div
                style={{
                  maxWidth: 420,
                  margin: "30px auto 0",
                  padding: "32px 24px",
                  textAlign: "center",
                  borderRadius: 20,
                  background: "rgba(15,47,24,0.9)",
                  border: "1px solid #1f5c31",
                }}
              >
                <div style={{ fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
                  Alarm sent
                </div>
                <div style={{ color: "#9fd3ae", fontSize: 14, marginBottom: 20 }}>
                  The siren is going off in his bedroom. Call him too — don't just
                  hit this and wait.
                </div>
                <span
                  onClick={reset}
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: 13,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  send another
                </span>
              </div>
            ) : (
              <>
                <p
                  style={{
                    textAlign: "center",
                    color: "rgba(255,255,255,0.55)",
                    marginBottom: 24,
                    fontSize: 14,
                  }}
                >
                  Press and hold for 1.5 seconds.
                </p>

                <div
                  onPointerDown={onDown}
                  onPointerUp={onUp}
                  onPointerCancel={onUp}
                  onPointerLeave={onUp}
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    maxWidth: 420,
                    height: 220,
                    margin: "0 auto",
                    borderRadius: 26,
                    background: progress > 0 ? "#8b0a12" : "#c1121f",
                    boxShadow: "0 10px 30px rgba(193,18,31,0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    WebkitTouchCallout: "none",
                    touchAction: "none",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      width: `${progress * 100}%`,
                      background: "#6d0710",
                      transition: "width 0.05s linear",
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      color: "#fff",
                      fontSize: 21,
                      fontWeight: 800,
                      letterSpacing: "0.02em",
                      padding: "0 16px",
                      textAlign: "center",
                    }}
                  >
                    {sending ? "SENDING..." : "HOLD TO WAKE WESTON"}
                  </div>
                </div>

                <div
                  style={{
                    minHeight: 24,
                    marginTop: 14,
                    textAlign: "center",
                    color: "#ff6b6b",
                    fontSize: 14,
                  }}
                >
                  {error}
                </div>

                <p
                  style={{
                    maxWidth: 420,
                    margin: "10px auto 0",
                    textAlign: "center",
                    color: "rgba(255,255,255,0.4)",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  This sets off a siren in his bedroom, turns the lights on, and
                  sends a critical alert to his phone that gets through silent
                  mode. It keeps going for 10 minutes.
                </p>
              </>
            )}
          </div>
        }
      />
    </div>
  );
}

export default DijonEmergency;
